import { endpoints } from './endpoints'
import type { TxApi } from '../core/tx'
import type { EncodeObject } from '@cosmjs/proto-signing'
import type { StdFee } from '@cosmjs/stargate'
import { useCallback, useState } from 'react'
import type { SignedTxResult, TxStage } from './types'
import { useMutation } from '@tanstack/react-query'

/** Adapter used by the pure signing pipeline. It is the only place where the
 * transport client and transaction core meet. */
export const tenantTxApi: TxApi = {
  account: async (address) => {
    const account = await endpoints.account(address)
    return { account_number: account.account_number, sequence: account.sequence }
  },
  broadcast: (txBytes) => endpoints.broadcast({ tx_bytes: txBytes }),
  status: (hash) => endpoints.txStatus(hash),
}

export interface SignedTxInput {
  signer: unknown
  address: string
  messages: EncodeObject[]
  fee: StdFee
  memo?: string
  chainId: string
  beforeBroadcast?: () => Promise<void>
}

export function useSignedTx() {
  const [stage, setStage] = useState<TxStage>('idle')
  const [receipt, setReceipt] = useState<{ txhash: string; succeeded: boolean; rawLog: string } | null>(null)
  const [lastInput, setLastInput] = useState<SignedTxInput | null>(null)
  const [lastHash, setLastHash] = useState<string | null>(null)
  const submit = useCallback(async (input: SignedTxInput): Promise<SignedTxResult> => {
    const { submit: signAndSubmit, waitForInclusion } = await import('../core/tx')
    setLastInput(input)
    setLastHash(null)
    setReceipt(null)
    setStage('awaiting-signature')
    try {
      const result = await signAndSubmit({ ...input, onBroadcast: () => setStage('broadcasting') }, tenantTxApi)
      if (!result.accepted) {
        setLastHash(result.txhash || null)
        setStage('rejected')
        setReceipt({ txhash: result.txhash, succeeded: false, rawLog: result.rawLog })
        return { ...result, included: false, succeeded: false }
      }
      setLastHash(result.txhash)
      setStage('confirming')
      const inclusion = await waitForInclusion(result.txhash, tenantTxApi)
      if (!inclusion.included) {
        setStage('timeout')
        setReceipt({ txhash: result.txhash, succeeded: false, rawLog: '' })
        return { ...result, included: false, succeeded: false }
      }
      const succeeded = result.accepted && inclusion.succeeded
      setStage(succeeded ? 'done' : 'rejected')
      setReceipt({ txhash: result.txhash, succeeded, rawLog: inclusion.rawLog || result.rawLog })
      return { ...result, included: true, succeeded }
    } catch (error) {
      setStage('rejected')
      throw error
    }
  }, [])
  const retry = useCallback(async () => lastInput ? submit(lastInput) : null, [lastInput, submit])
  const checkAgain = useCallback(async () => {
    if (!lastHash) return null
    const { waitForInclusion } = await import('../core/tx')
    setStage('confirming')
    try {
      const inclusion = await waitForInclusion(lastHash, tenantTxApi)
      const succeeded = inclusion.included && inclusion.succeeded
      setStage(inclusion.included ? (succeeded ? 'done' : 'rejected') : 'timeout')
      setReceipt({ txhash: lastHash, succeeded, rawLog: inclusion.rawLog || '' })
      return { txhash: lastHash, accepted: true, code: succeeded ? 0 : 1, rawLog: inclusion.rawLog || '', included: inclusion.included, succeeded }
    } catch (error) {
      setStage('timeout')
      throw error
    }
  }, [lastHash])
  const reset = useCallback(() => { setStage('idle'); setReceipt(null); setLastInput(null); setLastHash(null) }, [])
  return { submit, retry, checkAgain, stage, receipt, reset }
}

export function useOperatorAction() {
  return useMutation({
    mutationFn: ({ name, body }: { name: string; body?: unknown }) => endpoints.operator.action(name, body),
  })
}

export function useAdminRoleChange() {
  return useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: string }) => endpoints.operator.updateUserRole(userId, role),
  })
}

export function useProviderPower() {
  return useMutation({
    mutationFn: ({ providerId, action }: { providerId: number; action: 'stop' | 'start' | 'restart' | 'reboot' }) =>
      endpoints.operator.providerPower(providerId, action),
  })
}
